import { ContractTxAsyncActionResponse } from 'common/models/crypto';
import { Nullable } from 'common/models/optional';
import {
  ContextWithStringAmount,
  GeneralErrorEvent,
  GetStoredTxResponseEvent,
  XStateEvent,
} from 'common/models/xstate';
import { StakeApi } from 'sdk/stakeApi';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { InputUtils } from 'utils/inputUtils';
import { assign, interpret, Machine, spawn } from 'xstate';
import { NumberUtils } from 'utils/numberUtils';
import { StakeEvent } from './stakeEvent';
import { XStateUtils } from './xStateUtils';
import {
  createTxMachine,
  initialTxContext,
  RemoteEventExecutingTx,
  RemoteEventNoStoredTx,
  RemoteEventNoWalletConnected,
  RemoteEventTxError,
  RemoteEventTxSuccess,
  RemoteTxEvent,
} from './txMachine';
import { TransactionUtils } from 'utils/transactionUtils';
import { StorageUtils } from 'utils/storageUtils';
import { SwalUtils } from 'utils/swalUtils';
import { ChainId } from 'constants/constants';
import { Address } from 'viem';

enum StakeState {
  BASE_STATE = 'BASE_STATE',
  STAKE_AND_GET_TX = 'STAKE_AND_GET_TX',
  HANDLE_STAKE_TX = 'HANDLE_STAKE_TX',
  CHECK_INPUT = 'CHECK_INPUT',
  LOADING_DATA = 'LOADING__DATA',
  INVALID_INPUT = 'INVALID_INPUT',
  NO_WALLET_CONNECTED = 'NO_WALLET_CONNECTED',
  INSUFFICIENT_ICHI = 'INSUFFICIENT_ICHI',
  TOO_MANY_DECIMALS = 'TOO_MANY_DECIMALS',
  RESETTING_ICHI_ALLOWANCE = 'RESETTING_ICHI_ALLOWANCE',
  APPROVE_AND_GET_TX = 'APPROVE_AND_GET_TX',
  HANDLE_APPROVE_TX = 'HANDLE_APPROVE_TX',
  CHECKING_FOR_EXISTING_TX = 'CHECKING_FOR_EXISTING_TX',
}

enum StakeAction {
  STAKE_ERROR = 'STAKE_ERROR',
  APPROVE_ERROR = 'APPROVE_ERROR',
}

type StakeSchema = {
  states: {
    [s in StakeState]: object;
  };
};

type EventInputChangeArgs = {
  amount: string;
};

export type EventUpdateAccountInfoArgs = {
  address: Address;
  chainId: number;
};

export type EventLiquidityPercentageChangeArgs = {
  value: number;
};

// NOTE that xstate always whats the value to be named 'value', don't change the name,
// The actual value can be anything, but the variable has to be called value
type EventInputChange = XStateEvent<
  StakeEvent.INPUT_CHANGE,
  EventInputChangeArgs
>;
type EventLiquidityPercentageChange = XStateEvent<
  StakeEvent.LIQUIDITY_PERCENTAGE_CHANGE,
  EventLiquidityPercentageChangeArgs
>;
type EventUpdateAccountInfo = XStateEvent<
  StakeEvent.UPDATE_ACCOUNT_INFO,
  EventUpdateAccountInfoArgs
>;
type EventCheckInput = XStateEvent<StakeEvent.CHECK_INPUT>;
type EventStake = XStateEvent<StakeEvent.STAKE>;
type EventResetIchiAllowance = XStateEvent<StakeEvent.RESET_ICHI_ALLOWANCE>;
type EventApproveIchiAllowance = XStateEvent<StakeEvent.APPROVE_ICHI_ALLOWANCE>;

type StakeEvents =
  | EventLiquidityPercentageChange
  | EventInputChange
  | EventCheckInput
  | EventStake
  | EventResetIchiAllowance
  | EventApproveIchiAllowance
  | EventUpdateAccountInfo
  // Remote Tx Events
  | RemoteEventExecutingTx
  | RemoteEventTxError
  | RemoteEventTxSuccess
  | RemoteEventNoWalletConnected
  | RemoteEventNoStoredTx;

type StakeContext = {
  inputError?: string;
  accountAddress: Nullable<Address>;
  chainId: Nullable<number>;
  ichiPriceUsd: Nullable<number>;
  // Staking Amount
  amount: string;
  // Yes this works, but since we don't flow the xIchiPrice into the state machine, then it makes
  // more sense to keep it in the UnstakeModalContainer.tsx
  // amountUsd: number;
  storedTx: Nullable<StorageUtils.StoredTx>;
  liquidityPercentage: number;
  ichiBalance: bigint;
  stakeFee: number;
  gasLimit: bigint;
  isStaking: boolean;
  stakeResponse: Nullable<ContractTxAsyncActionResponse>;
  stakeError: Nullable<Error>;
  stakeDataResponse: Nullable<StakeApi.GetStakeDataResponse>;
  stakeDataError: Nullable<Error>;
  isIchiApproved: Nullable<boolean>;
  isSufficientIchi: Nullable<boolean>;
  isApprovingIchi: boolean;
  isResettingIchi: boolean;
  isLoadingData: boolean;
  txRef: any;
} & ContextWithStringAmount;

export const initialStakeContext: StakeContext = {
  accountAddress: null,
  chainId: null,
  ichiPriceUsd: null,
  amount: '',
  storedTx: null,
  liquidityPercentage: 100,
  ichiBalance: 0n,
  stakeFee: 0,
  gasLimit: CryptoUtils.calculateGasMargin(
    BigInt(GAS_SETTINGS.stake),
    ChainId.Mainnet
  ),
  isStaking: false,
  stakeResponse: null,
  stakeError: null,
  stakeDataResponse: null,
  stakeDataError: null,
  // Assume these are true so the UI will show Stake button first
  // If resolved to false before stakeing then the approval buttons
  // will be shown
  isSufficientIchi: true,
  isIchiApproved: true,
  isApprovingIchi: false,
  isResettingIchi: false,
  isLoadingData: false,
  txRef: null,
};

const isInsufficientIchi = (context: StakeContext) => {
  if (!context.stakeDataResponse) {
    return true; // true being insufficient ICHI
  }
  return !context.stakeDataResponse.isSufficientBalance;
};

type GetStakeDataResponseEvent = {
  type: string;
  data: StakeApi.GetStakeDataResponse;
};

const stakeApi = StakeApi.Stake.getInstance();

// https://github.com/ichifarm/ichi-app/blob/master/src/pages/Stake/index.tsx#L488
const stakeMachine = Machine<StakeContext, StakeSchema, StakeEvents>(
  {
    key: 'stake',
    id: 'stake',
    initial: StakeState.BASE_STATE,
    context: XStateUtils.shortStringify(initialStakeContext),
    states: {
      [StakeState.BASE_STATE]: {
        on: {
          [StakeEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            actions: assign({
              liquidityPercentage: (_, event) => event.value.value,
              // TODO: Note this is assuming 9 decimals which would not be the case on Polygon so fix before merging
              amount: (context, event) =>
                NumberUtils.amountBasedOnPercentage(
                  context.ichiBalance,
                  event.value.value,
                  9
                ),
              stakeError: () => null,
            }),
          },
          // Once the user has a connected wallet then let's check for existing transactions
          [StakeEvent.UPDATE_ACCOUNT_INFO]: {
            target: StakeState.LOADING_DATA,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
          [StakeEvent.STAKE]: {
            target: StakeState.STAKE_AND_GET_TX,
          },
          [StakeEvent.RESET_ICHI_ALLOWANCE]: {
            target: StakeState.RESETTING_ICHI_ALLOWANCE,
          },
          [StakeEvent.APPROVE_ICHI_ALLOWANCE]: {
            target: StakeState.APPROVE_AND_GET_TX,
          },
        },
      },
      // NOTE here I'm really assuming the only possible tx is staking, so if we want to support more types
      // in the future, would need to rework this some.
      [StakeState.CHECKING_FOR_EXISTING_TX]: {
        invoke: {
          src: (context) => {
            return TransactionUtils.getStoredTx({
              accountAddress: context.accountAddress as string,
              chainId: context.chainId as number,
              functionName: StakeApi.Stake.stakeFN,
            });
          },
          // Success means there is a mint tx, re-execute mint
          onDone: {
            target: StakeState.HANDLE_STAKE_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          // Error just means no tx found, go back to waiting for input
          onError: {
            target: StakeState.CHECK_INPUT,
          },
        },
      },
      [StakeState.STAKE_AND_GET_TX]: {
        entry: assign({
          isStaking: () => true,
        }),
        exit: assign({
          isStaking: () => false,
        }),
        invoke: {
          src: (context) => {
            return stakeApi.stake({
              chainId: context.chainId as number,
              accountAddress: context.accountAddress as Address,
              amount: context.amount,
            });
          },
          onDone: {
            // Here we aren't going to indicate success as this is 'detached' async response where
            // we have to wait for the actual tx to finish, which is being tracked in localstorage/indexDB
            // See the activities below
            target: StakeState.HANDLE_STAKE_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: StakeState.BASE_STATE,
            actions: [
              StakeAction.STAKE_ERROR,
              assign({
                stakeError: (_, event: GeneralErrorEvent) => event.data,
              }),
            ],
          },
        },
      },
      [StakeState.HANDLE_STAKE_TX]: {
        entry: assign({
          isStaking: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as StakeContext).chainId,
                accountAddress: (context as unknown as StakeContext)
                  .accountAddress,
                storedTx: (context as unknown as StakeContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as StakeContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isStaking: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: StakeState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: StakeState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: StakeState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            target: StakeState.LOADING_DATA,
          },
        },
      },
      [StakeState.RESETTING_ICHI_ALLOWANCE]: {
        entry: assign({
          isResettingIchi: () => true,
        }),
        exit: assign({
          isResettingIchi: () => false,
        }),
        invoke: {
          src: (context) => {
            return stakeApi.resetIchiAllowance({
              amount: context.amount,
              accountAddress: context.accountAddress as Address,
              chainId: context.chainId as number,
            });
          },
          onDone: {
            target: StakeState.BASE_STATE,
            actions: assign({
              isIchiApproved: () => false,
            }),
          },
          onError: {
            target: StakeState.BASE_STATE,
            actions: assign({
              isIchiApproved: (context) => context.isIchiApproved,
            }),
          },
        },
      },
      [StakeState.APPROVE_AND_GET_TX]: {
        entry: assign({
          isApprovingIchi: () => true,
        }),
        exit: assign({
          isApprovingIchi: () => false,
        }),
        invoke: {
          src: (context) => {
            return stakeApi.approveIchiAllowance({
              amount: context.amount,
              accountAddress: context.accountAddress as Address,
              chainId: context.chainId as number,
            });
          },
          onDone: {
            target: StakeState.HANDLE_APPROVE_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: StakeState.BASE_STATE,
            actions: [
              StakeAction.APPROVE_ERROR,
              assign({
                isIchiApproved: () => false,
              }),
            ],
          },
        },
      },
      [StakeState.HANDLE_APPROVE_TX]: {
        entry: assign({
          isApprovingIchi: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as StakeContext).chainId,
                accountAddress: (context as unknown as StakeContext)
                  .accountAddress,
                storedTx: (context as unknown as StakeContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as StakeContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isApprovingIchi: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: StakeState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: StakeState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: StakeState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            target: StakeState.LOADING_DATA,
          },
        },
      },
      [StakeState.LOADING_DATA]: {
        entry: assign({
          isLoadingData: () => true,
        }),
        exit: assign({
          isLoadingData: () => false,
        }),
        invoke: {
          // TODO: Put this in a service
          src: (context) => {
            return stakeApi.getStakeData({
              accountAddress: context.accountAddress as Address,
              chainId: context.chainId as number,
              amount: context.amount,
            });
          },
          onDone: {
            target: StakeState.CHECKING_FOR_EXISTING_TX,
            actions: assign({
              stakeDataResponse: (_, event: GetStakeDataResponseEvent) =>
                event.data,
              isIchiApproved: (_, event: GetStakeDataResponseEvent) =>
                event.data.isIchiApproved,
              isSufficientIchi: (_, event: GetStakeDataResponseEvent) =>
                event.data.isSufficientBalance,
              ichiBalance: (_, event: GetStakeDataResponseEvent) =>
                event.data.ichiBalance,
              ichiPriceUsd: (_, event: GetStakeDataResponseEvent) =>
                event.data.ichiPriceUsd,
              amount: (context, event: GetStakeDataResponseEvent) =>
                NumberUtils.amountBasedOnPercentage(
                  event.data.ichiBalance,
                  context.liquidityPercentage,
                  9
                ),
            }),
          },
          onError: {
            // TODO: Likely we should have another state to indicate to the user that there was an error fetching the metadata
            target: StakeState.CHECK_INPUT,
            actions: assign({
              stakeDataError: (_, event: GeneralErrorEvent) => event.data,
            }),
          },
        },
      },
      [StakeState.CHECK_INPUT]: {
        always: [
          {
            target: StakeState.BASE_STATE,
            cond: InputUtils.isDefaultInput,
          },
          {
            target: StakeState.BASE_STATE,
            cond: InputUtils.isZeroInput,
          },
          {
            target: StakeState.NO_WALLET_CONNECTED,
            cond: InputUtils.isNullChainId,
          },
          {
            target: StakeState.INSUFFICIENT_ICHI,
            cond: isInsufficientIchi,
          },
          {
            target: StakeState.TOO_MANY_DECIMALS,
            cond: InputUtils.isTooManyDecimals9,
          },
          {
            target: StakeState.INVALID_INPUT,
            cond: InputUtils.isInvalidInput,
          },
          {
            target: StakeState.BASE_STATE,
            cond: InputUtils.isValidInput,
          },
        ],
      },
      [StakeState.INVALID_INPUT]: {
        entry: assign({
          inputError: () => 'Invalid Input!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [StakeEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            target: StakeState.LOADING_DATA,
            actions: assign({
              amount: (context, event) =>
                NumberUtils.amountBasedOnPercentage(
                  context.ichiBalance,
                  event.value.value,
                  9
                ),
              stakeError: () => null,
            }),
          },
        },
      },
      [StakeState.INSUFFICIENT_ICHI]: {
        entry: assign({
          inputError: () => 'Insufficient ICHI!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [StakeEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            target: StakeState.LOADING_DATA,
            actions: assign({
              amount: (context, event) =>
                NumberUtils.amountBasedOnPercentage(
                  context.ichiBalance,
                  event.value.value,
                  9
                ),
              stakeError: () => null,
            }),
          },
        },
      },
      [StakeState.TOO_MANY_DECIMALS]: {
        entry: assign({
          inputError: () => 'Too many decimals!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [StakeEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            target: StakeState.LOADING_DATA,
            actions: assign({
              amount: (context, event) =>
                NumberUtils.amountBasedOnPercentage(
                  context.ichiBalance,
                  event.value.value,
                  9
                ),
              stakeError: () => null,
            }),
          },
        },
      },
      [StakeState.NO_WALLET_CONNECTED]: {
        entry: assign({
          inputError: () => 'Please connect a wallet!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [StakeEvent.LIQUIDITY_PERCENTAGE_CHANGE]: {
            target: StakeState.LOADING_DATA,
            actions: assign({
              amount: (context, event) =>
                NumberUtils.amountBasedOnPercentage(
                  context.ichiBalance,
                  event.value.value,
                  9
                ),
              stakeError: () => null,
            }),
          },
        },
      },
    },
  },
  {
    actions: {
      [StakeAction.STAKE_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Staking!',
        });
      },
      [StakeAction.APPROVE_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Approving!',
        });
      },
    },
  }
);

export const stakeService = interpret(stakeMachine, {
  // devTools: process.env.NODE_ENV === 'development'
}).start();

stakeService.subscribe((s) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[stakeService:debug]', s);
  }
});
